import React, { useState } from "react";
import DatePicker from "react-datepicker";
import { format, addDays, addHours, setHours, setMinutes, isBefore } from "date-fns";
import { fr } from "date-fns/locale";
import "react-datepicker/dist/react-datepicker.css";
import { cn } from "../../lib/utils";
import { useOccupiedSlots } from "../../hooks/useOccupiedSlots";
import { convertSlot, getTimeZoneLabel } from "../../utils/timezone";

const AppointmentCalendar = ({ onDateTimeSelect, selectedDateTime, token }) => {
  const [selectedDate, setSelectedDate] = useState(
    selectedDateTime?.date || null
  );
  const [selectedTime, setSelectedTime] = useState(
    selectedDateTime?.time || null
  );

  // Récupérer les créneaux occupés et les congés
  const {
    isSlotOccupied,
    isDateInHoliday,
    minDate: serverMinDate,
    timezone,
    loading: loadingSlots,
  } = useOccupiedSlots(token);

  // Les créneaux sont stockés en heure locale de l'expert. On les AFFICHE dans
  // le fuseau de l'assuré (un 09:00 parisien = 03:00 en Guadeloupe), mais on
  // continue de renvoyer la valeur d'origine au serveur.
  const isOtherTimeZone =
    timezone && timezone.expert && timezone.client &&
    timezone.expert !== timezone.client;

  const getDisplayTime = (date, time) => {
    if (!isOtherTimeZone || !date) return time;
    return convertSlot(
      format(date, "yyyy-MM-dd"),
      time,
      timezone.expert,
      timezone.client
    ).time;
  };

  // Date minimale : celle imposée par le serveur si elle existe (J+2 ouvrés
  // pour les RDV visio de région), sinon J+1. Le serveur refuse de toute façon
  // un créneau trop tôt : sans ça, le calendrier proposerait des créneaux que
  // la réservation rejetterait ensuite.
  const minSelectableDate = serverMinDate
    ? new Date(`${serverMinDate}T00:00:00`)
    : addDays(new Date(), 1);

  // Créneaux horaires disponibles (9h-17h avec pause déjeuner)
  const timeSlots = [
    "09:00",
    "09:45",
    "10:30",
    "11:15",
    "14:00",
    "14:45",
    "15:30",
    "16:15",
  ];

  // Vérifie si un créneau est dans les 24 prochaines heures
  const isSlotWithin24Hours = (date, time) => {
    if (!date) return false;
    const [hours, minutes] = time.split(":");
    const slotDateTime = setMinutes(setHours(date, parseInt(hours)), parseInt(minutes));
    const minDateTime = addHours(new Date(), 24);
    return isBefore(slotDateTime, minDateTime);
  };

  // Filtrer les dates disponibles (du lundi au vendredi, pas les week-ends, pas les congés)
  const filterDate = (date) => {
    // Exclure les week-ends (samedi = 6, dimanche = 0)
    const dayOfWeek = date.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      return false;
    }

    // Exclure les jours en congé
    if (isDateInHoliday(date)) {
      return false;
    }
    return true;
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    // Reset l'heure sélectionnée quand on change de date
    setSelectedTime(null);

    if (onDateTimeSelect) {
      onDateTimeSelect({
        date: date,
        time: null,
        formatted: null,
      });
    }
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);

    if (selectedDate && onDateTimeSelect) {
      const [hours, minutes] = time.split(":");
      const dateTime = setMinutes(
        setHours(selectedDate, parseInt(hours)),
        parseInt(minutes)
      );

      onDateTimeSelect({
        date: selectedDate,
        time: time,
        dateTime: dateTime,
        formatted: `${format(selectedDate, "EEEE d MMMM yyyy", {
          locale: fr,
        })} à ${time}`,
      });
    }
  };

  return (
    <div className="space-y-3">
      {/* Sélection de date */}
      <div className="space-y-1">
        <label className="text-xs font-medium text-gray-700">
          Choisir une date *
        </label>
        {loadingSlots && (
          <div className="text-xs text-gray-500 mb-2">
            Vérification des disponibilités...
          </div>
        )}
        <div className="relative">
          <DatePicker
            selected={selectedDate}
            onChange={handleDateChange}
            filterDate={filterDate}
            minDate={minSelectableDate} // J+1, ou J+2 ouvrés pour une visio de région
            maxDate={addDays(new Date(), 15)} // 15 jours à l'avance (J+15)
            locale={fr}
            dateFormat="EEEE d MMMM yyyy"
            placeholderText="Sélectionner une date"
            className="w-full h-9 px-3 text-sm border border-gray-300 rounded-md focus:border-red-500 focus:ring-1 focus:ring-red-500/20 focus:outline-none"
            calendarClassName="shadow-lg border-0"
            dayClassName={(date) =>
              cn(
                "hover:bg-red-100 rounded-md",
                selectedDate &&
                  date.toDateString() === selectedDate.toDateString() &&
                  "bg-red-600 text-white hover:bg-red-700",
                isDateInHoliday(date) &&
                  "bg-gray-200 text-gray-400 line-through cursor-not-allowed hover:bg-gray-200"
              )
            }
          />
        </div>
      </div>

      {/* Sélection de créneaux horaires */}
      {selectedDate && (
        <div className="space-y-1">
          <label className="text-xs font-medium text-gray-700">
            Choisir un créneau horaire (45 minutes) *
          </label>
          {loadingSlots && (
            <div className="text-xs text-gray-500 mb-2">
              Vérification des créneaux disponibles...
            </div>
          )}
          <div className="grid grid-cols-3 gap-1.5">
            {timeSlots
              .filter((time) => !isSlotOccupied(selectedDate, time) && !isSlotWithin24Hours(selectedDate, time)) // Filtrer les créneaux occupés et ceux dans les 24h
              .map((time) => (
                <button
                  key={time}
                  type="button"
                  onClick={() => handleTimeSelect(time)}
                  disabled={loadingSlots}
                  className={cn(
                    "p-2 text-xs border border-gray-300 rounded-md transition-colors",
                    "hover:border-red-500",
                    selectedTime === time
                      ? "bg-red-600 text-white border-red-600"
                      : "bg-white text-gray-700"
                  )}
                >
                  {getDisplayTime(selectedDate, time)}
                </button>
              ))}
          </div>
          {isOtherTimeZone && (
            <p className="text-xs text-gray-500 mt-1">
              Horaires affichés à l'heure de{" "}
              {getTimeZoneLabel(timezone.client)}.
            </p>
          )}
        </div>
      )}

      {/* Résumé de la sélection */}
      {selectedDate && selectedTime && (
        <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-md">
          <p className="text-xs text-green-800">
            <span className="font-medium">Rendez-vous sélectionné :</span>
            <br />
            {format(selectedDate, "EEEE d MMMM yyyy", { locale: fr })} à{" "}
            {getDisplayTime(selectedDate, selectedTime)}
            {isOtherTimeZone
              ? ` (heure de ${getTimeZoneLabel(timezone.client)})`
              : ""}
          </p>
        </div>
      )}

      {/* Légende */}
      {selectedDate && (
        <div className="mt-2 p-2 bg-gray-50 rounded-md border border-gray-200">
          <p className="text-xs text-gray-600 mb-1 font-medium">Légende :</p>
          <div className="flex flex-wrap gap-2 text-xs">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-white border border-gray-300 rounded"></div>
              <span className="text-gray-600">Disponible</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-gray-200 rounded"></div>
              <span className="text-gray-600">Fermé/Congé</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentCalendar;
